import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Home, Factory, ArrowRight, ArrowLeft, CheckCircle2, Copy, Search, AlertTriangle, Truck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { PlacesAutocomplete } from "@/components/PlacesAutocomplete";

const CATALOG = {
  Furniture: ["Sofa (1 Seater)", "Sofa (2 Seater)", "Sofa (3 Seater)", "Centre Table", "Side Table", "Dining Table", "Chair", "Bed (Single)", "Bed (Double)", "Baby Bed", "Deewan", "Cupboard (Small)", "Cupboard (Large)", "Shoe Rack", "Book Rack", "Dressing Table", "Study Table", "Wardrobe"],
  Electronics: ["TV (up to 32\")", "TV (43-55\")", "TV (65\"+)", "Fridge (Regular)", "Fridge (Large / Double Door)", "Washing Machine", "Dryer", "Dishwasher", "Music System", "Computer / Desktop", "Microwave", "A/C (Window)", "A/C (Split)", "Water Purifier", "Geyser"],
  Misc: ["Pots / Plants", "Suitcase", "Treadmill", "Cycle", "Fish Tank", "Carton 18\"x18\"x10\"", "Carton 18\"x18\"x20\"", "Carton 18\"x18\"x30\"", "Motorbike", "Scooter", "Mattress", "Almirah"],
  "🛍️ Clothing & Accessories": ["Shirts", "Pants", "Jackets", "Dresses", "Shoes", "Sneakers", "Boots", "Bags", "Belts", "Hats", "Scarves"],
  "🎁 Gifts & Personal Care": ["Novels", "Textbooks", "DVDs", "Video Games", "Board Games", "Plushies", "Action Figures", "Sealed Makeup", "Skincare Lotions", "Solid Soaps"],
  "🏠 Home & Electronics": ["Bedding", "Towels", "Curtains", "Smartphones", "Tablets", "Headphones", "Chargers", "Plastic Containers", "Mugs", "Non-sharp Utensils"],
};
const INDUSTRIAL_PRESETS = ["Machinery Unit", "Steel Coils", "Pallets", "Raw Material Sacks", "Generator", "Construction Equipment", "Chemical Drums", "Spare Parts Crate"];

const emptyForm = {
  customer_name: "", customer_phone: "", customer_email: "", customer_company: "", customer_designation: "",
  pickup_address: "", pickup_city: "", pickup_state: "", origin_floor: "", origin_lift: false,
  delivery_address: "", delivery_city: "", delivery_state: "", dest_floor: "", dest_lift: false,
  packing_date: "", moving_date: "", delivery_date: "",
  logistics: { storage: false, balcony_access: false, easy_loading: false, time_restrictions: false, difficult_items: false, multi_location: false },
  notes: "",
};

const STEPS = ["Contact", "Inventory", "Logistics"];

export default function PackersMoversFlow({ onBack }) {
  const [category, setCategory] = useState("household");
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLog = (k, v) => setForm((f) => ({ ...f, logistics: { ...f.logistics, [k]: v } }));
  const hasItem = (n) => items.some((i) => i.name === n);
  const toggleItem = (n) => setItems((a) => (a.some((i) => i.name === n) ? a.filter((i) => i.name !== n) : [...a, { name: n, quantity: 1, value: "" }]));
  const addCustom = (n) => { const v = n.trim(); if (!v) return; if (!hasItem(v)) setItems((a) => [...a, { name: v, quantity: 1, value: "" }]); setSearch(""); };
  const setItemField = (n, k, v) => setItems((a) => a.map((i) => (i.name === n ? { ...i, [k]: v } : i)));
  const removeItem = (n) => setItems((a) => a.filter((i) => i.name !== n));
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState({ name: "", quantity: "1", value: "" });
  const saveCustom = () => {
    const name = custom.name.trim();
    const qty = Number(custom.quantity);
    if (!name) return toast.error("Enter an item name");
    if (!qty || qty < 1) return toast.error("Enter a valid quantity (at least 1)");
    if (hasItem(name)) return toast.error("That item is already in your list");
    setItems((a) => [...a, { name, quantity: qty, value: custom.value === "" ? "" : Number(custom.value) || 0 }]);
    setCustom({ name: "", quantity: "1", value: "" });
    setCustomOpen(false);
    toast.success("Custom item added");
  };

  const next = () => {
    if (step === 1) {
      if (!form.customer_name.trim() || !form.customer_phone.trim()) return toast.error("Name and phone are required");
      if (!form.pickup_city.trim() || !form.delivery_city.trim()) return toast.error("Pickup and delivery city are required");
    }
    if (step === 2 && items.filter((i) => i.name.trim()).length === 0) return toast.error("Add at least one article");
    setStep((s) => Math.min(3, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    const cleaned = items.filter((i) => i.name.trim()).map((i) => ({ name: i.name, quantity: Number(i.quantity) || 1, value: Number(i.value) || 0 }));
    setLoading(true);
    try {
      const { data } = await api.post("/leads/submit", { category, ...form, move_date: form.moving_date || form.packing_date || "", items: cleaned });
      setDone(data);
      toast.success(`Request received · ${data.order_number}`);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setLoading(false); }
  };

  const reset = () => { setDone(null); setForm(emptyForm); setItems([]); setStep(1); setCategory("household"); };

  if (done) {
    const link = `${window.location.origin}/track/${done.track_token}`;
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="flex-1 grid place-items-center px-6 py-16">
          <div className="w-full max-w-lg border border-border bg-card p-10 text-center">
            <div className="h-14 w-14 bg-emerald-500/15 text-emerald-500 grid place-items-center mx-auto mb-6"><CheckCircle2 className="h-8 w-8" /></div>
            <h1 className="font-heading font-black text-3xl tracking-tight">Request Submitted</h1>
            <p className="text-muted-foreground mt-2">Your survey is now in our system. Our team will call you shortly with a quote.</p>
            <div className="mt-6 border border-border bg-muted p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Your Order Number</div>
              <div className="font-mono-num text-2xl font-bold text-primary mt-1" data-testid="submitted-order-number">{done.order_number}</div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <Input readOnly value={link} className="font-mono-num text-xs" data-testid="track-link" />
              <Button type="button" variant="outline" size="icon" className="rounded-none shrink-0" onClick={() => { navigator.clipboard.writeText(link); toast.success("Tracking link copied"); }}><Copy className="h-4 w-4" /></Button>
            </div>
            <div className="mt-6 flex gap-3">
              <Button asChild variant="outline" className="flex-1 rounded-none"><Link to={`/track/${done.track_token}`} data-testid="view-quote-status">Track quote</Link></Button>
              <Button className="flex-1 rounded-none" onClick={reset} data-testid="new-request">New request</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button type="button" onClick={onBack} data-testid="pm-back-to-services" className="mb-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-muted-foreground hover:text-primary transition-colors"><ArrowLeft className="h-4 w-4" /> Back to services</button>
        <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Detailed Inventory & Quote Survey</div>
        <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tight leading-[0.95]">Plan your move.</h1>
        <p className="text-muted-foreground mt-3">Give us the details for an accurate, all-inclusive quote from our verified partner network.</p>

        <div className="mt-6 inline-grid grid-cols-2 border border-border bg-card" data-testid="category-tabs">
          {[{ k: "household", label: "Household Items", icon: Home }, { k: "industrial", label: "Industrial Materials", icon: Factory }].map((t) => (
            <button key={t.k} type="button" data-testid={`tab-${t.k}`} onClick={() => { setCategory(t.k); setItems([]); }}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-wide transition-colors ${category === t.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Stepper */}
        <div className="mt-8 flex items-center gap-2" data-testid="wizard-stepper">
          {STEPS.map((label, i) => {
            const n = i + 1;
            return (
              <div key={label} className="flex items-center gap-2 flex-1">
                <div className={`flex items-center gap-2 ${n <= step ? "text-primary" : "text-muted-foreground"}`}>
                  <span className={`h-7 w-7 grid place-items-center text-xs font-bold border ${n < step ? "bg-primary text-primary-foreground border-primary" : n === step ? "border-primary" : "border-border"}`}>{n < step ? "✓" : n}</span>
                  <span className="text-xs font-bold uppercase tracking-wide hidden sm:block">{label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px ${n < step ? "bg-primary" : "bg-border"}`} />}
              </div>
            );
          })}
        </div>

        <div className="mt-6 space-y-6" data-testid="public-form">
          {step === 1 && (
            <>
              <section className="border border-border bg-card p-6 space-y-4">
                <h2 className="font-heading font-bold text-xl uppercase tracking-tight">Contact details</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Full name" v={form.customer_name} on={(v) => set("customer_name", v)} id="customer_name" />
                  <Field label="Telephone" v={form.customer_phone} on={(v) => set("customer_phone", v)} id="customer_phone" />
                  <Field label="Email" v={form.customer_email} on={(v) => set("customer_email", v)} id="customer_email" />
                  <Field label="Company" v={form.customer_company} on={(v) => set("customer_company", v)} id="customer_company" />
                  <Field label="Designation" v={form.customer_designation} on={(v) => set("customer_designation", v)} id="customer_designation" />
                </div>
              </section>
              <div className="grid md:grid-cols-2 gap-4">
                <section className="border border-border bg-card p-6 space-y-3">
                  <h3 className="font-heading font-bold uppercase tracking-tight">Origin</h3>
                  <div className="space-y-2">
                    <Label htmlFor="pickup_address">Address</Label>
                    <PlacesAutocomplete id="pickup_address" value={form.pickup_address} placeholder="Start typing an address…" testid="field-pickup_address"
                      onChange={(v) => set("pickup_address", v)}
                      onSelect={(p) => setForm((f) => ({ ...f, pickup_address: p.formatted_address || f.pickup_address, pickup_city: p.city || f.pickup_city, pickup_state: p.state || f.pickup_state }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="City" v={form.pickup_city} on={(v) => set("pickup_city", v)} id="pickup_city" />
                    <Field label="State" v={form.pickup_state} on={(v) => set("pickup_state", v)} id="pickup_state" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 items-end">
                    <Field label="Floor" v={form.origin_floor} on={(v) => set("origin_floor", v)} id="origin_floor" />
                    <YesNo label="Lift available" value={form.origin_lift} onChange={(v) => set("origin_lift", v)} testid="origin-lift" />
                  </div>
                </section>
                <section className="border border-border bg-card p-6 space-y-3">
                  <h3 className="font-heading font-bold uppercase tracking-tight">Destination</h3>
                  <div className="space-y-2">
                    <Label htmlFor="delivery_address">Address</Label>
                    <PlacesAutocomplete id="delivery_address" value={form.delivery_address} placeholder="Start typing an address…" testid="field-delivery_address"
                      onChange={(v) => set("delivery_address", v)}
                      onSelect={(p) => setForm((f) => ({ ...f, delivery_address: p.formatted_address || f.delivery_address, delivery_city: p.city || f.delivery_city, delivery_state: p.state || f.delivery_state }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="City" v={form.delivery_city} on={(v) => set("delivery_city", v)} id="delivery_city" />
                    <Field label="State" v={form.delivery_state} on={(v) => set("delivery_state", v)} id="delivery_state" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 items-end">
                    <Field label="Floor" v={form.dest_floor} on={(v) => set("dest_floor", v)} id="dest_floor" />
                    <YesNo label="Lift available" value={form.dest_lift} onChange={(v) => set("dest_lift", v)} testid="dest-lift" />
                  </div>
                </section>
              </div>
              <section className="border border-border bg-card p-6 space-y-3">
                <h3 className="font-heading font-bold uppercase tracking-tight">Preferred dates</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <DateField label="Packing" v={form.packing_date} on={(v) => set("packing_date", v)} id="packing_date" />
                  <DateField label="Moving" v={form.moving_date} on={(v) => set("moving_date", v)} id="moving_date" />
                  <DateField label="Delivery" v={form.delivery_date} on={(v) => set("delivery_date", v)} id="delivery_date" />
                </div>
              </section>
            </>
          )}

          {step === 2 && (
            <section className="border border-border bg-card p-6">
              <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-1">{category === "household" ? "Household articles" : "Materials"}</h2>
              <p className="text-sm text-muted-foreground mb-4">Search and add articles, set quantity, and declare a value for insurance.</p>

              {category === "household" ? (
                <>
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input className="rounded-none pl-9" placeholder="Search articles… (e.g. sofa, fridge, carton)" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="article-search"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(search); } }} />
                  </div>
                  {search.trim() && !Object.values(CATALOG).flat().some((n) => n.toLowerCase() === search.trim().toLowerCase()) && (
                    <button type="button" onClick={() => addCustom(search)} className="mb-4 text-xs border border-primary text-primary px-3 py-1.5 hover:bg-primary hover:text-primary-foreground transition-colors" data-testid="add-custom-article">+ Add "{search.trim()}"</button>
                  )}
                  <div className="space-y-4">
                    {Object.entries(CATALOG).map(([grp, list]) => {
                      const shown = list.filter((n) => n.toLowerCase().includes(search.toLowerCase()));
                      if (!shown.length) return null;
                      return (
                        <div key={grp}>
                          <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{grp}</div>
                          <div className="flex flex-wrap gap-2">
                            {shown.map((n) => (
                              <button key={n} type="button" onClick={() => toggleItem(n)} data-testid={`article-${n}`}
                                className={`text-xs px-3 py-1.5 border transition-colors ${hasItem(n) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
                                {hasItem(n) ? "✓ " : "+ "}{n}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="flex flex-wrap gap-2 mb-4">
                  {INDUSTRIAL_PRESETS.map((p) => (
                    <button key={p} type="button" onClick={() => toggleItem(p)} data-testid={`article-${p}`}
                      className={`text-xs px-3 py-1.5 border transition-colors ${hasItem(p) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
                      {hasItem(p) ? "✓ " : "+ "}{p}
                    </button>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => setCustomOpen(true)}
                data-testid="add-extra-item-btn"
                className="mt-6 w-full flex items-center justify-center gap-2 border-2 border-dashed border-primary text-primary font-bold uppercase tracking-wide py-3 hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                <Plus className="h-4 w-4" /> Add Extra Item (Not in List)
              </button>

              <Dialog open={customOpen} onOpenChange={setCustomOpen}>
                <DialogContent className="max-w-md rounded-none" data-testid="custom-item-dialog">
                  <DialogHeader><DialogTitle className="font-heading text-2xl uppercase tracking-tight">Add Custom Item</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Item Name *</Label>
                      <Input className="rounded-none h-10" value={custom.name} placeholder="e.g. Aquarium, Treadmill" onChange={(e) => setCustom({ ...custom, name: e.target.value })} data-testid="custom-item-name"
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveCustom(); } }} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Quantity *</Label>
                        <Input type="number" min="1" className="rounded-none h-10 font-mono-num" value={custom.quantity} onChange={(e) => setCustom({ ...custom, quantity: e.target.value })} data-testid="custom-item-qty" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Declared Value (₹)</Label>
                        <Input type="number" min="0" placeholder="0" className="rounded-none h-10 font-mono-num" value={custom.value} onChange={(e) => setCustom({ ...custom, value: e.target.value })} data-testid="custom-item-value" />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-2">
                    <Button type="button" variant="outline" className="rounded-none" onClick={() => setCustomOpen(false)}>Cancel</Button>
                    <Button type="button" className="rounded-none font-bold uppercase" onClick={saveCustom} data-testid="custom-item-save">Add Item</Button>
                  </div>
                </DialogContent>
              </Dialog>

              <div className="mt-6">
                <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Selected articles ({items.length})</div>
                {items.length === 0 ? (
                  <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No articles selected yet.</div>
                ) : (
                  <div className="border border-border divide-y divide-border" data-testid="selected-articles">
                    <div className="hidden sm:grid grid-cols-[1fr_90px_140px_40px] gap-2 px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground bg-muted">
                      <span>Article</span><span>Qty</span><span>Value ₹</span><span></span>
                    </div>
                    {items.map((it) => (
                      <div key={it.name} className="grid grid-cols-[1fr_70px_110px_36px] sm:grid-cols-[1fr_90px_140px_40px] gap-2 items-center px-3 py-2" data-testid={`selected-${it.name}`}>
                        <span className="text-sm">{it.name}</span>
                        <Input type="number" min="1" value={it.quantity} onChange={(e) => setItemField(it.name, "quantity", e.target.value)} className="rounded-none h-9" data-testid={`qty-${it.name}`} />
                        <Input type="number" min="0" placeholder="0" value={it.value} onChange={(e) => setItemField(it.name, "value", e.target.value)} className="rounded-none h-9 font-mono-num" data-testid={`value-${it.name}`} />
                        <Button type="button" variant="ghost" size="icon" className="rounded-none h-9 w-9 text-destructive" onClick={() => removeItem(it.name)} data-testid={`remove-${it.name}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {step === 3 && (
            <>
              <section className="border border-border bg-card p-6">
                <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-1">Important information</h2>
                <p className="text-sm text-muted-foreground mb-4">Help us plan the right crew and vehicle.</p>
                <div className="grid md:grid-cols-2 gap-3">
                  <YesNo label="Storage needed?" desc="Temporary warehousing required" value={form.logistics.storage} onChange={(v) => setLog("storage", v)} testid="log-storage" block />
                  <YesNo label="Balcony access?" desc="Items to be moved via balcony" value={form.logistics.balcony_access} onChange={(v) => setLog("balcony_access", v)} testid="log-balcony" block />
                  <YesNo label="Easy loading/unloading?" desc="Truck can park close by" value={form.logistics.easy_loading} onChange={(v) => setLog("easy_loading", v)} testid="log-loading" block />
                  <YesNo label="Time restrictions?" desc="Society/office timing limits" value={form.logistics.time_restrictions} onChange={(v) => setLog("time_restrictions", v)} testid="log-time" block />
                  <YesNo label="Difficult items?" desc="Piano, safe, large glass, etc." value={form.logistics.difficult_items} onChange={(v) => setLog("difficult_items", v)} testid="log-difficult" block />
                  <YesNo label="Multi-location pickup/delivery?" desc="More than one address" value={form.logistics.multi_location} onChange={(v) => setLog("multi_location", v)} testid="log-multi" block />
                </div>
                <div className="space-y-2 mt-4">
                  <Label htmlFor="notes">Special instructions</Label>
                  <Textarea id="notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-none" rows={3} placeholder="Anything else our team should know?" data-testid="field-notes" />
                </div>
              </section>

              <div className="border-2 border-red-500/60 bg-red-500/5 p-5 flex gap-3" data-testid="gas-warning">
                <AlertTriangle className="h-6 w-6 text-red-500 shrink-0" />
                <div>
                  <div className="font-heading font-bold uppercase tracking-tight text-red-500">Important — Gas Cylinders Prohibited</div>
                  <p className="text-sm text-muted-foreground mt-1">Filled or empty LPG/gas cylinders <span className="font-bold text-foreground">cannot</span> be transported due to licensing and insurance restrictions. Please empty and hand over cylinders to your gas agency before the move.</p>
                </div>
              </div>
            </>
          )}

          {/* Nav */}
          <div className="flex items-center justify-between gap-3">
            {step > 1
              ? <Button type="button" variant="outline" className="rounded-none gap-2" onClick={back} data-testid="wizard-back"><ArrowLeft className="h-4 w-4" /> Back</Button>
              : <span />}
            {step < 3
              ? <Button type="button" className="rounded-none gap-2 font-bold uppercase tracking-wide" onClick={next} data-testid="wizard-next">Continue <ArrowRight className="h-4 w-4" /></Button>
              : <Button type="button" disabled={loading} onClick={submit} className="rounded-none gap-2 font-bold uppercase tracking-wide" data-testid="submit-quote">{loading ? "Submitting…" : <>Submit survey <ArrowRight className="h-4 w-4" /></>}</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, on, id }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={v} onChange={(e) => on(e.target.value)} className="rounded-none" data-testid={`field-${id}`} />
    </div>
  );
}

function DateField({ label, v, on, id }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="date" value={v} onChange={(e) => on(e.target.value)} className="rounded-none" data-testid={`field-${id}`} />
    </div>
  );
}

function YesNo({ label, desc, value, onChange, testid, block }) {
  return (
    <div className={`flex items-center justify-between gap-3 ${block ? "border border-border p-3" : ""}`}>
      <div><div className="text-sm font-medium">{label}</div>{desc && <div className="text-xs text-muted-foreground">{desc}</div>}</div>
      <div className="flex border border-border shrink-0">
        <button type="button" onClick={() => onChange(true)} className={`px-3 py-1.5 text-xs font-bold uppercase ${value ? "bg-emerald-500 text-white" : "text-muted-foreground hover:text-foreground"}`} data-testid={`${testid}-yes`}>Yes</button>
        <button type="button" onClick={() => onChange(false)} className={`px-3 py-1.5 text-xs font-bold uppercase ${!value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`${testid}-no`}>No</button>
      </div>
    </div>
  );
}

function Header() {
  return (
    <header className="h-16 border-b border-border bg-card sticky top-0 z-20 flex items-center justify-between px-6">
      <div className="flex items-center gap-2"><Logo className="h-9" /></div>
      <div className="flex items-center gap-3">
        <ThemeToggle />
        <Link to="/track-shipment" className="text-sm font-bold uppercase tracking-wide bg-primary text-primary-foreground px-4 py-2 hover:opacity-90 transition-opacity flex items-center gap-2" data-testid="track-consignment-link"><Truck className="h-4 w-4" /><span className="hidden sm:inline">Track Consignment</span><span className="sm:hidden">Track</span></Link>
        <Link to="/login" className="text-sm font-bold uppercase tracking-wide border border-border px-4 py-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors hidden sm:inline-block" data-testid="staff-login-link">Staff login</Link>
      </div>
    </header>
  );
}
